// The `reviews` block — ONE markup for every surface.
//
// `ReviewsShell` is the markup the shared `initReviews`
// (reviews.ts) takes over: the React surfaces call it from an effect with a
// transport built on the host's `PageDataBlocksContext`; the published static
// page renders the same shell server-side (site-ssr/renderSite.tsx) and
// `reviewsScript()` wires it there with its own fetch transport. The
// designer canvas (no transport) shows sample reviews so the author sees the
// design.
import React, { useContext, useEffect, useRef } from 'react'
import type { ReviewsBlock } from '@twasta/page-schema'

import { substituteVars } from './actions/substitute'
import { PageDataBlocksContext, type PageDataBlocksApi } from './commerceApi'
import { initReviews, type ReviewsTransport } from './reviews'

const INPUT: React.CSSProperties = {
  padding: '9px 12px', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box',
  border: '1px solid var(--pg-border, #e2e8f0)', borderRadius: 8,
  background: 'var(--pg-surface, #fff)', color: 'var(--pg-text, inherit)', fontFamily: 'inherit',
}
const HONEYPOT: React.CSSProperties = {
  position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden',
}
const MUTED = 'var(--pg-text-secondary, #64748b)'

const SAMPLE = [
  { rating: 5, title: 'Excellent quality', body: 'Approved reviews appear here, newest first.', name: 'A customer', date: 'Today' },
  { rating: 4, title: '', body: 'A new review waits for approval before it is shown.', name: 'Another customer', date: 'Last week' },
]

function starText(n: number): string {
  let s = ''
  for (let i = 1; i <= 5; i++) s += i <= Math.round(n) ? '★' : '☆'
  return s
}

export interface ReviewsShellProps {
  block: ReviewsBlock
  /** The resolved record (token already substituted). */
  record: string
  /** Static page: the absolute reviews endpoint the script calls. */
  endpoint?: string
  /** Static page: localStorage key of the site session bearer. */
  tokenKey?: string
  /** Static page: where "Sign in" goes (`?next=` is appended). */
  signInHref?: string
  /** Designer canvas: draw sample reviews instead of an empty shell. */
  design?: boolean
}

/** The markup `initReviews` wires. Pure — safe for SSR. */
export function ReviewsShell({ block, record, endpoint, tokenKey, signInHref, design }: ReviewsShellProps) {
  const showSummary = block.showSummary !== false
  const sample = design ? SAMPLE : []
  return (
    <section data-pg-reviews="" data-record={record || undefined} data-endpoint={endpoint || undefined}
             data-token-key={tokenKey || undefined} data-signin-href={signInHref || undefined}
             style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}>
      {/* `initReviews` shows/hides parts with the `hidden` attribute, and most
          of them carry an inline `display` — which beats the UA's [hidden]
          rule, so a signed-out visitor saw the write form anyway. One scoped
          rule makes the attribute authoritative on every surface. */}
      <style>{'[data-pg-reviews] [hidden]{display:none!important}'}</style>
      <h2 style={{ margin: 0, fontSize: '1.3rem' }}>{block.heading || 'Reviews'}</h2>
      {showSummary && (
        <div data-pg-reviews-summary="" hidden={!design}
             style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 120 }}>
            <span data-pg-reviews-avg="" style={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1 }}>{design ? '4.5' : ''}</span>
            <span data-pg-reviews-stars="" style={{ color: 'var(--pg-accent, #f59e0b)', letterSpacing: 2 }}>{design ? starText(4.5) : ''}</span>
            <span data-pg-reviews-count="" style={{ fontSize: '0.8rem', color: MUTED }}>{design ? '2 reviews' : ''}</span>
          </div>
          <div data-pg-reviews-hist="" style={{ flex: '1 1 220px', display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 360 }} />
        </div>
      )}
      <ol data-pg-reviews-list="" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {sample.map((r, i) => (
          <li key={i} style={{ padding: '12px 14px', border: '1px solid var(--pg-border, #e2e8f0)', borderRadius: 10 }}>
            <span style={{ color: 'var(--pg-accent, #f59e0b)', letterSpacing: 2 }}>{starText(r.rating)}</span>
            {r.title && <strong style={{ marginLeft: 8 }}>{r.title}</strong>}
            <p style={{ margin: '6px 0', whiteSpace: 'pre-wrap' }}>{r.body}</p>
            <span style={{ fontSize: '0.8rem', color: MUTED }}>{r.name} · {r.date}</span>
          </li>
        ))}
      </ol>
      <template data-pg-reviews-item="" dangerouslySetInnerHTML={{ __html:
        '<li style="padding:12px 14px;border:1px solid var(--pg-border,#e2e8f0);border-radius:10px">'
        + '<span data-f="stars" style="color:var(--pg-accent,#f59e0b);letter-spacing:2px"></span> '
        + '<strong data-f="title" style="margin-left:8px"></strong>'
        + '<p data-f="body" style="margin:6px 0;white-space:pre-wrap"></p>'
        + '<span style="font-size:.8rem;color:var(--pg-text-secondary,#64748b)"><span data-f="name"></span> '
        + '<span data-f="date"></span></span> '
        + '<span data-f="verified" hidden style="margin-left:6px;font-size:.72rem;font-weight:600;'
        + 'color:var(--pg-success,#15803d);background:rgba(22,163,74,.12);border-radius:999px;padding:1px 8px">'
        + 'Verified purchase</span></li>' }} />
      <p data-pg-reviews-empty="" hidden={design}
         style={{ margin: 0, color: MUTED }}>{block.emptyText || 'No reviews yet — be the first to write one.'}</p>
      <button type="button" data-pg-reviews-more="" hidden
              style={{ alignSelf: 'flex-start', padding: '8px 14px', border: '1px solid var(--pg-border, #e2e8f0)',
                       borderRadius: 8, background: 'transparent', color: 'inherit', cursor: 'pointer' }}>
        Show more reviews
      </button>
      <form data-pg-reviews-form="" hidden={!design}
            style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 16,
                     border: '1px solid var(--pg-border, #e2e8f0)', borderRadius: 12, position: 'relative' }}
            onSubmit={design ? (e) => e.preventDefault() : undefined}>
        <strong>{block.formHeading || 'Write a review'}</strong>
        <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'flex', gap: 4, alignItems: 'center' }}>
          <legend style={{ fontSize: '0.85rem', color: MUTED, marginBottom: 4 }}>Your rating</legend>
          {[1, 2, 3, 4, 5].map((n) => (
            <label key={n} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginRight: 8, fontSize: '0.9rem', cursor: 'pointer' }}>
              <input type="radio" name="rating" value={n} required={n === 1} />
              <span aria-hidden="true" style={{ color: 'var(--pg-accent, #f59e0b)' }}>{'★'.repeat(n)}</span>
              <span style={{ position: 'absolute', left: -9999 }}>{n} star{n > 1 ? 's' : ''}</span>
            </label>
          ))}
        </fieldset>
        <input name="title" maxLength={160} placeholder="Title (optional)" aria-label="Review title" style={INPUT} />
        <textarea name="body" required maxLength={4000} rows={4} placeholder="What did you think?" aria-label="Your review" style={INPUT} />
        <div data-pg-reviews-name-row="" hidden>
          <input name="name" maxLength={80} placeholder="Your name" aria-label="Your name" style={INPUT} />
        </div>
        <div aria-hidden="true" style={HONEYPOT}>
          <input name="_pg_hp" tabIndex={-1} autoComplete="off" />
        </div>
        <button type="submit" style={{
          alignSelf: 'flex-start', padding: '10px 18px', fontWeight: 600, border: 'none', borderRadius: 8,
          background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)', cursor: 'pointer',
        }}>{block.submitLabel || 'Submit review'}</button>
        <p data-pg-reviews-status="" role="status" hidden style={{ margin: 0, fontSize: '0.88rem' }} />
      </form>
      <p data-pg-reviews-reason="" hidden style={{ margin: 0, fontSize: '0.9rem', color: MUTED }} />
      <p data-pg-reviews-signin="" hidden style={{ margin: 0, fontSize: '0.9rem', color: MUTED }}>
        <a data-pg-reviews-signin-link="" href={signInHref || '#'} style={{ color: 'var(--pg-link, var(--pg-primary))', fontWeight: 600 }}>
          Sign in
        </a>{' '}to write a review.
      </p>
    </section>
  )
}

function transportFor(api: PageDataBlocksApi, blockId: string): ReviewsTransport {
  return {
    list: (record, offset) => api.listReviews(blockId, record, offset),
    add: (payload) => api.addReview(blockId, payload),
    signIn: api.signIn,
  }
}

/** The React surfaces: the shell, wired by the shared function. */
export function ReviewsRenderer({ block, vars, design = false }: {
  block: ReviewsBlock
  vars?: Record<string, unknown> | ((key: string) => unknown)
  design?: boolean
}) {
  const api = useContext(PageDataBlocksContext)
  const ref = useRef<HTMLDivElement | null>(null)
  const record = substituteVars(block.record || '', vars || {}).trim()
  const live = !design && !!api && !!record && !record.includes('{{')

  useEffect(() => {
    if (!live || !ref.current) return
    const root = ref.current.querySelector('[data-pg-reviews]') as HTMLElement | null
    if (root) initReviews(root, transportFor(api!, block.id))
  }, [live, api, block.id, record])

  if (!design && !api) {
    return (
      <div style={{ padding: 12, border: '1px dashed var(--pg-border, #e2e8f0)', borderRadius: 8, color: MUTED, fontSize: 13 }}>
        Reviews are not available here.
      </div>
    )
  }
  // `key` gives each record a fresh shell, so the wiring (which runs once per
  // element) starts again when the page moves to another product.
  return (
    <div ref={ref} style={{ display: 'contents' }}>
      <ReviewsShell key={record} block={block} record={record} design={design || !live} />
    </div>
  )
}
